import { CellProps } from 'react-table';
import { useApolloClient, useReactiveVar } from '@apollo/client';
import { InputField, Typography } from '@vartanainc/design-system';
import { get } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as SearchIcon } from '../../assets/search.svg';
import { HasPermission } from '../../components/HasPermission/HasPermission';
import Loader from '../../components/Loader';
import ResetUserPasswordModal from '../../components/Modals/ResetUserPasswordModal';
import NoDataState from '../../components/NoDataState/NoDataState';
import Table from '../../components/Table';
import { sessionVar } from '../../graphql/cache';
import { GET_USER_BY_NUMBER } from '../../graphql/queries/user';
import {
  capitalizeUsername,
  getUSDateFormat,
  humanizeName,
  showErrorToast,
  sortByDate,
} from '../../utils/helpers';

interface User {
  id: string;
  fullName: string;
  number: string;
  userRole?: {
    id: string;
  };
}

interface SessionData {
  session?: {
    user: User;
  };
}

interface UserRole {
  name: string;
}

interface ManagedUser {
  number: string;
  fullName: string;
  email: string;
  title: string;
  updatedAt: string;
  userRole: UserRole;
}

interface UserByNumberData {
  userByNumber: {
    managedUsers: ManagedUser[];
  };
}

interface TableUser {
  name: {
    name: string;
    email: string;
  };
  jobTitle: string;
  role: string;
  updateOn: {
    formatted: string;
  };
  email?: string;
  number?: string;
  userRole: UserRole;
}

const MyTeamList: React.FC = () => {
  const client = useApolloClient();
  const sessionData: SessionData = useReactiveVar(sessionVar);
  const currentUser = get(sessionData, 'session.user') as User;
  const userNumber: string | undefined = currentUser?.number;

  const [loading, setLoading] = useState<boolean>(true);
  const [selectedUser, setSelectedUser] = useState<Partial<ManagedUser>>({});
  const [userData, setUserData] = useState<ManagedUser[] | null>(null);
  const [searchUserName, setSearchUserName] = useState<string>('');
  const [isResetUserPasswordDialogOpen, setIsResetUserPasswordDialogOpenOpen] =
    useState<boolean>(false);

  const handleResetPassword = (selectedUserParam: ManagedUser): void => {
    setSelectedUser(selectedUserParam);
    setIsResetUserPasswordDialogOpenOpen(true);
  };

  useEffect(() => {
    const fetchUser = async (): Promise<void> => {
      if (!userNumber) return;

      try {
        const { data } = await client.query<UserByNumberData>({
          query: GET_USER_BY_NUMBER,
          variables: { number: userNumber },
        });

        setUserData(data.userByNumber.managedUsers);
      } catch (error) {
        const errorMessage = get(error, 'message', '');
        reportError(errorMessage);
        showErrorToast();
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [client, userNumber]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'User',
        accessor: 'name.name',
        Cell: ({ row }: CellProps<TableUser>) => {
          const { original } = row;
          return (
            <div className="flex items-start flex-col gap-1">
              <div>
                <Link
                  to="/dashboard/settings/users/view"
                  className="vp-text-link-bold text-vartana-blue-60"
                  state={{ selectedUser: original }}
                >
                  <Typography variant="paragraph14" bold>
                    {original.name.name}
                  </Typography>
                </Link>
              </div>
              <Typography variant="paragraph12">{original.name.email}</Typography>
            </div>
          );
        },
      },
      {
        Header: 'Job title',
        accessor: 'jobTitle',
        Cell: ({ value }: { value: string }) => (
          <Typography variant="paragraph14">{value || '--'}</Typography>
        ),
      },
      {
        Header: 'Role',
        accessor: 'role',
        disableSortBy: true,
        Cell: ({ row }: CellProps<TableUser>) => {
          const { original } = row;
          return (
            <Typography variant="paragraph14">
              {original.userRole.name || '--'}
            </Typography>
          );
        },
      },
      {
        Header: 'Updated on',
        accessor: 'updateOn.formatted',
        sortType: sortByDate,
        Cell: ({ value }: { value: string }) => (
          <Typography variant="paragraph14">{value}</Typography>
        ),
      },
      {
        Header: '',
        accessor: 'resetPassword',
        disableSortBy: true,
        Cell: ({ row }: CellProps<TableUser>) => {
          const { original } = row;
          return (
            <HasPermission resource="user" action="reset_password">
              <button onClick={() => handleResetPassword(original)}>
                <span className="vp-text-link-bold text-vartana-blue-60">
                  Reset password
                </span>
              </button>
            </HasPermission>
          );
        },
      },
    ];
  }, []);

  const data = useMemo<TableUser[]>(() => {
    if (!Array.isArray(userData)) return [];

    return userData
      .filter((user) =>
        get(user, 'fullName', '').toLowerCase().includes(searchUserName.toLowerCase())
      )
      .map((user) => ({
        ...user,
        name: {
          name: capitalizeUsername(get(user, 'fullName', '--')),
          email: get(user, 'email', ''),
        },
        jobTitle: get(user, 'title', '--'),
        role: humanizeName(get(user, 'userRole.name', '--')),
        updateOn: {
          formatted: getUSDateFormat(get(user, 'updatedAt', '')),
        },
      }));
  }, [userData, searchUserName]);

  if (loading) {
    return <Loader containerClassName="absolute top-1/2 left-1/2" />;
  }

  const showRecords = (): JSX.Element => {
    if (!userData || data.length === 0) {
      return (
        <div className="flex justify-center items-center w-full h-[calc(100%-10rem)]">
          <NoDataState />
        </div>
      );
    }

    return (
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-ml-6 lg:-ml-8">
          <div className="py-2 align-middle inline-block min-w-full sm:pl-6 lg:pl-8">
            <div className="overflow-hidden border-b border-gray-200 sm:rounded-none">
              <Table
                cellClassName="px-8"
                columns={columns}
                data={data}
                getHeaderProps={() => ({
                  className:
                    'px-8 py-4 text-left text-xs font-medium text-vartana-gray-60 tracking-wider uppercase',
                })}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-white shadow rounded-none">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex">
            <div className="w-full max-w-md">
              <InputField
                label="Search sales rep"
                name="user-name"
                fullWidth
                prefixIcon={<SearchIcon />}
                onChange={(e) => setSearchUserName(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* TODO: shahmir811 will try to make single div class for the below styling */}

      <div className="mt-2 bg-white shadow rounded-none">
        <div className="-my-2 overflow-x-auto sm:-ml-6 lg:-ml-8">
          <div className="align-middle inline-block min-w-full sm:pl-6 lg:pl-8">
            {showRecords()}
          </div>
        </div>
      </div>

      <ResetUserPasswordModal
        open={isResetUserPasswordDialogOpen}
        selectedUser={{
          email: selectedUser.email || '',
          number: selectedUser.number || '',
        }}
        handleClose={() => setIsResetUserPasswordDialogOpenOpen(false)}
      />
    </>
  );
};

export default MyTeamList;
